/**
 * rc129 — Ajustes › Portal del cliente.
 *
 * Las claves `portal.*` existen en BD desde la mig 034 (v1.1.0) pero nunca
 * tuvieron pantalla: sesión, textos de contacto y datos genéricos de pago que
 * el cliente ve en "Cómo pagar". Aquí viven todas. El nombre que ve el cliente
 * NO se configura aquí: sale de la razón social de su empresa (Ajustes ›
 * Empresas) y, antes de iniciar sesión, del nombre comercial del ISP.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Smartphone, Clock, Headset, Wallet, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useConfigByGrupo, useUpdateConfig, type ConfigItem } from "@/features/config/api";
import type { ApiError } from "@/lib/api";

const K_SESION = "portal.session_lifetime_minutes";
const K_SLA = "portal.sla_texto";
const K_CLABE = "portal.cobro_clabe";
const K_OXXO = "portal.cobro_oxxo";
const K_TEL = "portal.contacto_telefono";
const K_OFI = "portal.contacto_oficina";

const TEXT_FIELDS: { key: string; label: string; placeholder?: string; hint?: string }[] = [
  { key: K_TEL, label: "Teléfono de contacto", placeholder: "442 000 0000",
    hint: "Se muestra en el login y en \"olvidé mi contraseña\"." },
  { key: K_OFI, label: "Oficina (dirección corta)", placeholder: "Av. Principal 123, Col. Centro",
    hint: "Para cuando el cliente necesita ir a regenerar su acceso." },
  { key: K_CLABE, label: "CLABE genérica (opcional)", placeholder: "18 dígitos",
    hint: "Solo si cobras por transferencia a una cuenta común. Vacío = no se muestra. La CLABE personal de cada cliente sale de Toku, no de aquí." },
  { key: K_OXXO, label: "Referencia OXXO (opcional)", placeholder: "Tarjeta o referencia",
    hint: "Vacío = no se muestra." },
];

export function PortalClienteSection() {
  const { data: items, isLoading } = useConfigByGrupo("portal");
  const updateMut = useUpdateConfig();
  const [values, setValues] = useState<Record<string, string>>({});
  const [sesionMin, setSesionMin] = useState("480");
  const [sla, setSla] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!items) return;
    const v: Record<string, string> = {};
    items.forEach((it: ConfigItem) => { v[it.key] = typeof it.value === "string" ? it.value : String(it.value ?? ""); });
    setValues(v);
    setSesionMin(String(items.find((i) => i.key === K_SESION)?.value ?? 480));
    setSla(String(items.find((i) => i.key === K_SLA)?.value ?? ""));
    setDirty(false);
  }, [items]);

  function upd(key: string, val: string) {
    setValues((s) => ({ ...s, [key]: val }));
    setDirty(true);
  }

  async function handleSave() {
    const min = Number(sesionMin);
    if (!Number.isInteger(min) || min < 5 || min > 43200) {
      toast.error("Duración de sesión inválida", { description: "Entre 5 minutos y 43 200 (30 días)." });
      return;
    }
    const clabe = (values[K_CLABE] ?? "").replace(/\s+/g, "");
    if (clabe && !/^\d{18}$/.test(clabe)) {
      toast.error("CLABE inválida", { description: "Deben ser exactamente 18 dígitos, o vacía." });
      return;
    }
    try {
      await updateMut.mutateAsync({ key: K_SESION, value: min, value_type: "int", grupo: "portal",
        descripcion: "Duración de la sesión del portal del cliente en minutos." });
      await updateMut.mutateAsync({ key: K_SLA, value: sla.trim(), value_type: "string", grupo: "portal",
        descripcion: "Texto de tiempo estimado de respuesta al abrir un ticket." });
      for (const f of TEXT_FIELDS) {
        const raw = f.key === K_CLABE ? clabe : (values[f.key] ?? "").trim();
        await updateMut.mutateAsync({ key: f.key, value: raw, value_type: "string", grupo: "portal" });
      }
      toast.success("Portal del cliente guardado");
      setDirty(false);
    } catch (e) {
      toast.error("Error al guardar", { description: (e as ApiError).message });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  const horas = Number(sesionMin) > 0 ? (Number(sesionMin) / 60).toFixed(Number(sesionMin) % 60 ? 1 : 0) : "—";

  return (
    <div className="flex flex-col gap-4">
      {/* Marca */}
      <div className="rounded-lg border bg-card p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Building2 size={14} className="text-primary" />
          <Label className="text-sm font-semibold">Nombre que ve el cliente</Label>
        </div>
        <p className="text-xs text-muted-foreground">
          El portal se presenta con la <b>razón social de la empresa</b> a la que pertenece cada
          cliente (un cliente de una empresa ve esa marca, no la del ISP). Antes de iniciar
          sesión usa el <b>nombre comercial</b> de Datos del ISP. Se editan en{" "}
          <Link to="/ajustes/empresas" className="text-primary underline">Empresas</Link> y{" "}
          <Link to="/ajustes/isp" className="text-primary underline">Datos del ISP</Link>.
        </p>
      </div>

      {/* Sesión */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-primary" />
          <Label className="text-sm font-semibold">Sesión del cliente</Label>
        </div>
        <div className="flex items-center gap-3">
          <Input type="number" min={5} max={43200} value={sesionMin}
            onChange={(e) => { setSesionMin(e.target.value); setDirty(true); }}
            className="w-28 font-mono" />
          <span className="text-xs text-muted-foreground">minutos · {horas} h</span>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Tiempo que el cliente permanece conectado sin volver a escribir su contraseña.
          Aplica a los inicios de sesión nuevos.
        </p>
      </div>

      {/* Soporte */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Headset size={14} className="text-primary" />
          <Label className="text-sm font-semibold">Soporte</Label>
        </div>
        <div>
          <Label className="text-xs">Tiempo estimado de respuesta (texto que ve el cliente al abrir un ticket)</Label>
          <Input value={sla} onChange={(e) => { setSla(e.target.value); setDirty(true); }}
            placeholder="menos de 2 horas en horario laboral (8am–8pm)" />
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {TEXT_FIELDS.filter((f) => f.key === K_TEL || f.key === K_OFI).map((f) => (
            <div key={f.key}>
              <Label className="text-xs">{f.label}</Label>
              <Input value={values[f.key] ?? ""} onChange={(e) => upd(f.key, e.target.value)} placeholder={f.placeholder} />
              {f.hint && <p className="mt-1 text-[11px] text-muted-foreground">{f.hint}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Cómo pagar */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Wallet size={14} className="text-primary" />
          <Label className="text-sm font-semibold">"Cómo pagar" — datos genéricos</Label>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {TEXT_FIELDS.filter((f) => f.key === K_CLABE || f.key === K_OXXO).map((f) => (
            <div key={f.key}>
              <Label className="text-xs">{f.label}</Label>
              <Input value={values[f.key] ?? ""} onChange={(e) => upd(f.key, e.target.value)}
                placeholder={f.placeholder} className={f.key === K_CLABE ? "font-mono" : undefined} />
              {f.hint && <p className="mt-1 text-[11px] text-muted-foreground">{f.hint}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <Smartphone size={12} /> Los cambios se ven en el portal al recargar.
        </span>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-muted-foreground">Cambios sin guardar</span>}
          <Button size="sm" onClick={handleSave} disabled={!dirty || updateMut.isPending}>
            <Save size={12} />
            {updateMut.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </div>
    </div>
  );
}
